import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Base Skeleton component with pulse animation
 */
export const Skeleton = ({ className, ...props }) => (
  <div
    className={cn(
      'animate-pulse rounded-md bg-gray-200',
      className
    )}
    {...props}
  />
);

/**
 * Product Card Skeleton
 */
export const ProductCardSkeleton = () => (
  <div className="bg-white rounded-xl overflow-hidden shadow-sm">
    {/* Image placeholder */}
    <Skeleton className="w-full h-48" />
    
    <div className="p-4 space-y-3">
      {/* Category */}
      <Skeleton className="h-3 w-16 rounded" />
      
      {/* Title */}
      <Skeleton className="h-5 w-3/4 rounded" />
      
      {/* Price */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-20 rounded" />
        <Skeleton className="h-9 w-9 rounded-full" />
      </div>
    </div>
  </div>
);

/**
 * Product Grid Skeleton
 */
export const ProductGridSkeleton = ({ count = 8 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <ProductCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * User Table Row Skeleton
 */
export const TableRowSkeleton = ({ columns = 5 }) => (
  <tr className="border-b">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton className="h-4 w-full max-w-[100px] rounded" />
      </td>
    ))}
  </tr>
);

/**
 * Table Skeleton
 */
export const TableSkeleton = ({ rows = 5, columns = 5 }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead>
        <tr className="border-b bg-gray-50">
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i} className="px-4 py-3 text-left">
              <Skeleton className="h-4 w-20 rounded" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <TableRowSkeleton key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Order Card Skeleton
 */
export const OrderCardSkeleton = () => (
  <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
    {/* Header */}
    <div className="flex justify-between items-start">
      <div className="space-y-2">
        <Skeleton className="h-5 w-32 rounded" />
        <Skeleton className="h-4 w-24 rounded" />
      </div>
      <Skeleton className="h-6 w-20 rounded-full" />
    </div>
    
    {/* Items */}
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        <Skeleton className="w-16 h-16 rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4 rounded" />
          <Skeleton className="h-3 w-1/2 rounded" />
        </div>
      </div>
    </div>
    
    {/* Footer */}
    <div className="pt-4 border-t flex justify-between">
      <Skeleton className="h-4 w-24 rounded" />
      <Skeleton className="h-5 w-20 rounded" />
    </div>
  </div>
);

/**
 * Order List Skeleton
 */
export const OrderListSkeleton = ({ count = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <OrderCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Profile Card Skeleton
 */
export const ProfileCardSkeleton = () => (
  <div className="bg-white rounded-xl p-6 shadow-sm">
    <div className="flex items-center gap-4 mb-6">
      {/* Avatar */}
      <Skeleton className="w-20 h-20 rounded-full" />
      
      <div className="space-y-2">
        <Skeleton className="h-6 w-40 rounded" />
        <Skeleton className="h-4 w-48 rounded" />
      </div>
    </div>
    
    {/* Info rows */}
    <div className="space-y-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex justify-between">
          <Skeleton className="h-4 w-20 rounded" />
          <Skeleton className="h-4 w-32 rounded" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Stats Card Skeleton
 */
export const StatsCardSkeleton = () => (
  <div className="bg-white rounded-xl p-6 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <Skeleton className="h-4 w-24 rounded" />
      <Skeleton className="w-10 h-10 rounded-full" />
    </div>
    <Skeleton className="h-8 w-20 rounded mb-2" />
    <Skeleton className="h-3 w-32 rounded" />
  </div>
);

/**
 * Dashboard Stats Skeleton
 */
export const DashboardStatsSkeleton = ({ count = 4 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <StatsCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Cart Item Skeleton
 */
export const CartItemSkeleton = () => (
  <div className="flex gap-4 py-4 border-b">
    <Skeleton className="w-20 h-20 rounded-lg" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-32 rounded" />
      <Skeleton className="h-3 w-20 rounded" />
      <div className="flex justify-between items-center">
        <Skeleton className="h-8 w-24 rounded" />
        <Skeleton className="h-5 w-16 rounded" />
      </div>
    </div>
  </div>
);

/**
 * Blog Post Card Skeleton
 */
export const BlogCardSkeleton = () => (
  <div className="bg-white rounded-xl overflow-hidden shadow-sm">
    <Skeleton className="w-full h-48" />
    <div className="p-5 space-y-3">
      <div className="flex gap-3">
        <Skeleton className="h-3 w-20 rounded" />
        <Skeleton className="h-3 w-24 rounded" />
      </div>
      <Skeleton className="h-5 w-full rounded" />
      <Skeleton className="h-4 w-3/4 rounded" />
      <Skeleton className="h-4 w-1/2 rounded" />
    </div>
  </div>
);

/**
 * Blog Grid Skeleton
 */
export const BlogGridSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <BlogCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Page Header Skeleton
 */
export const PageHeaderSkeleton = () => (
  <div className="space-y-4 mb-8">
    <Skeleton className="h-8 w-48 rounded" />
    <Skeleton className="h-4 w-96 rounded" />
  </div>
);

/**
 * Form Field Skeleton
 */
export const FormFieldSkeleton = () => (
  <div className="space-y-2">
    <Skeleton className="h-4 w-24 rounded" />
    <Skeleton className="h-10 w-full rounded-lg" />
  </div>
);

/**
 * Form Skeleton
 */
export const FormSkeleton = ({ fields = 4 }) => (
  <div className="space-y-6">
    {Array.from({ length: fields }).map((_, i) => (
      <FormFieldSkeleton key={i} />
    ))}
    <Skeleton className="h-10 w-32 rounded-lg" />
  </div>
);

/**
 * Generic Content Skeleton Block
 */
export const ContentBlockSkeleton = ({ lines = 4 }) => (
  <div className="space-y-3">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className="h-4 rounded"
        style={{ width: `${Math.random() * 30 + 70}%` }}
      />
    ))}
  </div>
);

/**
 * Full Page Loading Skeleton
 */
export const PageLoadingSkeleton = () => (
  <div className="min-h-screen bg-gray-50 p-8">
    <div className="max-w-7xl mx-auto">
      <PageHeaderSkeleton />
      <DashboardStatsSkeleton count={4} />
      <div className="mt-8">
        <TableSkeleton rows={5} columns={6} />
      </div>
    </div>
  </div>
);

export default {
  Skeleton,
  ProductCardSkeleton,
  ProductGridSkeleton,
  TableRowSkeleton,
  TableSkeleton,
  OrderCardSkeleton,
  OrderListSkeleton,
  ProfileCardSkeleton,
  StatsCardSkeleton,
  DashboardStatsSkeleton,
  CartItemSkeleton,
  BlogCardSkeleton,
  BlogGridSkeleton,
  PageHeaderSkeleton,
  FormFieldSkeleton,
  FormSkeleton,
  ContentBlockSkeleton,
  PageLoadingSkeleton,
};
